import React, { createContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { readSportsPageState } from '../pages/Sports/_helpers/sportsPageState';
import { toDisplayOddsDecimal } from '../pages/Sports/_helpers/oddsFormat';

/** 베팅 카트 상태 관리 — 타입별 분리 */
export const SportsBetCartContext = createContext();

const EMPTY_CARTS = { DOMESTIC: [], INTERNATIONAL: [], SPECIAL: [], LIVE: [] };
const initialSportsPageState = readSportsPageState();

const isSameCartId = (left, right) => {
  if (left == null || right == null) return false;

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber === rightNumber;
  }

  return String(left) === String(right);
};

export const SportsBetCartProvider = ({ children }) => {
  const [cartsByType, setCartsByType] = useState({ ...EMPTY_CARTS });
  const cartsByTypeRef = useRef(cartsByType);
  const [currentType, setCurrentType] = useState(
    () => initialSportsPageState?.bettingType || 'DOMESTIC'
  );
  const [stake, setStake] = useState('');

  /** 현재 타입의 선택지 */
  const selections = cartsByType[currentType] || [];

  useEffect(() => {
    cartsByTypeRef.current = cartsByType;
  }, [cartsByType]);

  const updateCartsByType = useCallback((updater) => {
    setCartsByType((previous) => {
      const next = updater(previous);
      cartsByTypeRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const matchIds = [...new Set(
      (cartsByType.LIVE || [])
        .map((selection) => Number(selection?.match_id ?? selection?.matchId))
        .filter((value) => Number.isFinite(value))
    )];
    window.dispatchEvent(new CustomEvent('sp_cart_match_ids', { detail: { matchIds } }));
  }, [cartsByType]);

  /** 다른 타입 카트 건수 (배지용) */
  const otherTypeCounts = useMemo(() => {
    const counts = {};
    Object.entries(cartsByType).forEach(([type, items]) => {
      if (items.length > 0) counts[type] = items.length;
    });
    return counts;
  }, [cartsByType]);

  /** 선택지 토글 (현재 타입 카트에서) */
  const toggleSelection = useCallback((sel) => {
    updateCartsByType(prev => {
      const normalizedSelection = {
        ...sel,
        odds: toDisplayOddsDecimal(sel?.odds) ?? Number(sel?.odds),
      };
      const cart = prev[currentType] || [];
      const exists = cart.find(s => isSameCartId(s.odds_id, normalizedSelection.odds_id));
      if (exists) {
        return { ...prev, [currentType]: cart.filter(s => !isSameCartId(s.odds_id, normalizedSelection.odds_id)) };
      }
      /** 같은 경기의 같은 logical market이면 교체 (market_group_key 기준) */
      const groupKey = normalizedSelection.market_group_key;
      const filtered = groupKey
        ? cart.filter(s => s.market_group_key !== groupKey)
        : cart.filter(s => !(s.market_id && isSameCartId(s.market_id, normalizedSelection.market_id)));
      return { ...prev, [currentType]: [...filtered, normalizedSelection] };
    });
  }, [currentType, updateCartsByType]);

  /** 선택지 제거 */
  const removeSelection = useCallback((oddsId) => {
    updateCartsByType(prev => {
      const cart = prev[currentType] || [];
      return { ...prev, [currentType]: cart.filter(s => !isSameCartId(s.odds_id, oddsId)) };
    });
  }, [currentType, updateCartsByType]);

  /** 현재 타입 카트 초기화 */
  const clearCart = useCallback(() => {
    updateCartsByType(prev => ({ ...prev, [currentType]: [] }));
    setStake('');
  }, [currentType, updateCartsByType]);

  const replaceSelectionsForType = useCallback((type, nextSelections = []) => {
    if (!Object.prototype.hasOwnProperty.call(EMPTY_CARTS, type)) return;
    updateCartsByType(prev => ({
      ...prev,
      [type]: Array.isArray(nextSelections) ? nextSelections : [],
    }));
  }, [updateCartsByType]);

  /** 선택지가 카트에 있는지 확인 */
  const isSelected = useCallback((oddsId) => {
    return (cartsByType[currentType] || []).some(s => isSameCartId(s.odds_id, oddsId));
  }, [cartsByType, currentType]);

  const isSelectionInAnyCart = useCallback((oddsId) => (
    Object.values(cartsByTypeRef.current).some((cart) => (
      cart.some((selection) => isSameCartId(selection.odds_id, oddsId))
    ))
  ), []);

  /** 배당 변경 시 카트 내 선택지 업데이트.
   * A selection's ACCEPTED price (`odds`, used for totalOdds/potentialWin) is
   * never silently changed here — only the flash fires immediately, and a
   * persistent `status` is set (`locked` | `unavailable` | `odds_changed`).
   * The new price is held in `pendingOdds` until the user explicitly calls
   * acceptOddsChange, or removes the selection outright. This mirrors a
   * standard sportsbook betslip: odds moving must never silently change what
   * the user is about to stake on. */
  const [oddsChanges, setOddsChanges] = useState({});
  const updateOddsInCart = useCallback((oddsId, newOdds, patch = {}) => {
    let found = false;
    updateCartsByType(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(type => {
        updated[type] = updated[type].map(s => {
          if (!isSameCartId(s.odds_id, oddsId)) return s;
          found = true;

          const oldOdds = Number(s.odds);
          const numNew = toDisplayOddsDecimal(newOdds);
          const hasNewOdds = Number.isFinite(numNew);
          const priceChanged = hasNewOdds && Math.abs(oldOdds - numNew) > 0.001;

          if (priceChanged) {
            /** 변동 방향 기록 — 2초 플래시 (락/미제공 여부와 무관하게 항상 표시) */
            setOddsChanges(ch => ({ ...ch, [oddsId]: numNew > oldOdds ? 'up' : 'down' }));
            setTimeout(() => {
              setOddsChanges(ch => { const n = { ...ch }; delete n[oddsId]; return n; });
            }, 2000);
          }

          const isLocked = patch.bettable === false || patch.locked === true || patch.is_suspended === true;
          const isUnavailable = patch.unavailable === true;

          let nextStatus = s.status ?? 'active';
          if (isUnavailable) nextStatus = 'unavailable';
          else if (isLocked) nextStatus = 'locked';
          else if (priceChanged) nextStatus = 'odds_changed';
          else if (s.status === 'locked' && patch.bettable === true) {
            // Reopened — if the price also moved while locked, surface that
            // as odds_changed instead of silently going back to active.
            nextStatus = s.pendingOdds != null ? 'odds_changed' : 'active';
          }

          return {
            ...s,
            ...patch,
            status: nextStatus,
            pendingOdds: nextStatus === 'odds_changed' && hasNewOdds
              ? numNew
              : (nextStatus === 'active' ? undefined : s.pendingOdds),
            odds: s.odds, // frozen until acceptOddsChange — must win over any of the above
          };
        });
      });
      return updated;
    });
    return found;
  }, [updateCartsByType]);

  /** 사용자가 변경된 배당을 명시적으로 수락 — pendingOdds를 odds로 반영 */
  const acceptOddsChange = useCallback((oddsId) => {
    updateCartsByType(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(type => {
        updated[type] = updated[type].map(s => {
          if (!isSameCartId(s.odds_id, oddsId)) return s;
          if (s.pendingOdds == null) return { ...s, status: 'active' };
          return { ...s, odds: toDisplayOddsDecimal(s.pendingOdds) ?? s.pendingOdds, pendingOdds: undefined, status: 'active' };
        });
      });
      return updated;
    });
  }, [updateCartsByType]);

  /** 총 배당 (조합) */
  const totalOdds = useMemo(() => {
    if (selections.length === 0) return 0;
    return selections.reduce((acc, s) => acc * (toDisplayOddsDecimal(s.odds) ?? 1), 1);
  }, [selections]);

  /** 예상 당첨금 (다폴더 보너스 미적용 — 기본) */
  const potentialWinRaw = useMemo(() => {
    const numStake = Number(stake) || 0;
    const betType = selections.length <= 1 ? 'SINGLE' : 'COMBO';
    if (betType === 'SINGLE') {
      return selections.reduce((sum, s) => sum + numStake * Number(s.odds), 0);
    }
    return numStake * totalOdds;
  }, [selections, stake, totalOdds]);

  /** 다폴더 보너스 적용 potentialWin (외부에서 bonusMultiplier 주입) */
  const [bonusMultiplier, setBonusMultiplier] = useState(1);
  const potentialWin = useMemo(() => {
    return Math.floor(potentialWinRaw * bonusMultiplier);
  }, [potentialWinRaw, bonusMultiplier]);

  const value = {
    selections, stake, setStake,
    currentType, setCurrentType, otherTypeCounts,
    toggleSelection, removeSelection, clearCart, replaceSelectionsForType, isSelected, isSelectionInAnyCart,
    totalOdds, potentialWin, potentialWinRaw, bonusMultiplier, setBonusMultiplier, updateOddsInCart, oddsChanges,
    acceptOddsChange,
  };

  return <SportsBetCartContext.Provider value={value}>{children}</SportsBetCartContext.Provider>;
};
